import { Tooltip } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { SingleDatePicker } from 'react-dates';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import AddBuyerAddress from '@components/AddBuyerAddress';
import CustomDropdown from '@components/CustomDropdown';
import LoaderComponent from '@components/LoaderComponent';
import TimeBasedLoader from '@components/TimeBasedLoader';
import { toastError, toastSuccess } from '@components/Toast';

import {
	fetchBeneficiaryList,
	fetchBrandLocaionList,
	fetchInvoiceDetails,
	fetchNitexBankDetails,
	fetchOrderPlaceWorkflow
} from '@store/orders/actions';
import {
	getBeneficiaryList,
	getBrandLocationList,
	getInvoiceDetails,
	getNitexBankDetails
} from '@store/reducers';

import ApiClient from '@lib/apiClient';

import {
	_getKey,
	capitalizeFirstLetter,
	changeDateFormat,
	checkAnyUserType,
	checkSingular,
	convertNumberToUSFormat,
	getShortName,
	isValidFile,
	isValidInput,
	packTag,
	setTag,
	validateFloatNumber,
	validateNumber
} from '@utils/Util';

import { BASE_URL, VALID_FILE_SIZE } from '@/constants/constant';

const ProformaInvoice = ({ orderId, orderStage, stylesCount }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [, setInitialLoading] = useState(false);
	const [isEditDate, setIsEditDate] = useState(false);
	const [selectedByerAddress, setSelectedBuyerAddress] = useState('');
	const [selectedByerShippingAddress, setSelectedBuyerShippingAddress] = useState('');
	const [selectedBeneficiary, setSelectedBeneficiary] = useState('');
	const [selectedBankDetails, setSelectedBankDetails] = useState('');
	const [isAddAddress, setIsAddAddress] = useState(false);
	const [invoiceDetails, setInvoiceDetails] = useState(null);
	const [cellId, setCellId] = useState('');
	const [, setIsUpdateInvoice] = useState(false);
	const [isAdNewInvoice, setIsAddNewInvoice] = useState(false);
	const [, setSelectAll] = useState(false);
	const [startLoader, setStartLoader] = useState(false);
	const [newTermCellId, setNewTermCellId] = useState('');
	const [, setIsUpdateTerm] = useState(false);
	const [isAddNewTerm, setIsAddNewTerm] = useState(false);
	const [allInvoiceList, setAllInvoiceList] = useState([]);
	const [allOrdersInvoiceList, setAllOrdersInvoiceList] = useState([]);
	const [date, setDate] = useState(null);
	const [searchPi, setSearchPi] = useState('');
	const dispatch = useDispatch();
	const location = useLocation();
	const [focused, setFocused] = useState(false);

	let invoiceInfo = useSelector(getInvoiceDetails);
	let brandLocationList = useSelector(getBrandLocationList);
	let beneficiaryList = useSelector(getBeneficiaryList);
	let nitexBankDetails = useSelector(getNitexBankDetails);
	const prevTarget = useRef(null);

	const getCard = (item) => {
		return `<div>
				<div>
					${item?.orderName} (${item?.orderRefNumber})
				</div>
				<div>
					${item.invoiceNumber} • ${item?.totalQuantity} Pcs •
					$${parseFloat(item?.totalAmount).toFixed(2)}
				</div>
			</div>`;
	};

	const onGetAllOrdersInvoiceList = async () => {
		setIsLoading(true);
		await ApiClient.GET(
			'invoiceAction',
			`filter?brandId=${invoiceInfo?.brandId}&page=0&size=30&search=${searchPi}`
		)
			.then(({ data }) => {
				setIsLoading(false);
				let list = data?.data.map((item) => ({
					...item,
					value: `${item.orderName} ${item.invoiceNumber} ${item.invoiceNumber}`
				}));
				setAllOrdersInvoiceList(list);
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const getInvoiceDetailsInfo = (id) => {
		dispatch(fetchInvoiceDetails(id)).finally(() => {
			setInitialLoading(false);
		});
	};

	const onInvoiceDetailsClick = (item) => {
		if (invoiceDetails.id !== item.id) {
			getInvoiceDetailsInfo(item.id);
		}
	};

	const getAllInvoiceList = async (id, isInitialRender = false) => {
		setIsLoading(true);
		await ApiClient.GET('getProjectInvoice', `${id}/all`)
			.then(({ data }) => {
				setIsLoading(false);
				setAllInvoiceList(data);
				if (isInitialRender) {
					let proformaId = new URLSearchParams(location.search).get('invoiceId');
					if (proformaId) {
						return getInvoiceDetailsInfo(proformaId);
					}
					return getInvoiceDetailsInfo(data[0].id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};
	const onSearchPi = (value) => setSearchPi(value);

	useEffect(() => {
		getAllInvoiceList(orderId, true);
		dispatch(fetchBeneficiaryList()).finally(() => {
			setInitialLoading(false);
		});
		dispatch(fetchNitexBankDetails()).finally(() => {
			setInitialLoading(false);
		});
	}, [orderId]);

	useEffect(() => {
		if (invoiceInfo?.id) {
			setInvoiceDetails(invoiceInfo);
		}
	}, [invoiceInfo]);

	useEffect(() => {
		if (invoiceDetails?.brandId) {
			onGetAllOrdersInvoiceList();
		}
	}, [invoiceDetails?.id, searchPi]);

	useEffect(() => {
		if (invoiceDetails?.brandId) {
			dispatch(fetchBrandLocaionList(invoiceDetails.brandId)).finally(() => {
				setInitialLoading(false);
			});
			setSelectedBuyerAddress(invoiceDetails?.buyerBillingLocation?.id);
			setSelectedBeneficiary(invoiceDetails?.beneficiaryDetails?.title);
			setSelectedBuyerShippingAddress(invoiceDetails?.buyerShippingLocation?.id);
			setSelectedBankDetails(invoiceDetails?.beneficiaryBankDetails?.id);
			initializeSelectAll();
		}
	}, [invoiceDetails]);

	const initializeSelectAll = () => {
		let val = false;
		for (let i = 0; i < invoiceDetails?.termsAndConditionsResponseList?.length; i++) {
			let item = invoiceDetails?.termsAndConditionsResponseList?.[i];
			val = val || item.isDeleted;
		}
		setSelectAll(val);
	};

	const onUpdateInvoice = async (body) => {
		setIsLoading(true);
		await ApiClient.POST('editInvoice', body, invoiceDetails?.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					toastSuccess(data.message);
					dispatch(fetchOrderPlaceWorkflow(orderId));
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUpdateBuyerDetails = async (locationId, addressType) => {
		setIsLoading(true);
		const params = `${invoiceDetails?.id}/${locationId}?addressType=${addressType}`;
		await ApiClient.PUT('updateBillingShippingAddress', {}, params)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					dispatch(fetchOrderPlaceWorkflow(orderId));
					toastSuccess(data.message);
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUpdateBeneficiaryList = async (item) => {
		setIsLoading(true);
		await ApiClient.PUT('updateBeneficiaryList', { ...item }, invoiceDetails?.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					dispatch(fetchOrderPlaceWorkflow(orderId));
					toastSuccess(data.message);
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUpdateNitexBankDetails = async (item) => {
		setIsLoading(true);
		await ApiClient.PUT('updateNitexBankDetails', { ...item }, invoiceDetails?.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					dispatch(fetchOrderPlaceWorkflow(orderId));
					toastSuccess(data.message);
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onAddItemToInvoice = async (item) => {
		setIsLoading(true);
		const body = {
			name: item.itemName,
			quantity: item.quantity,
			unitPrice: item.unitPrice
		};
		await ApiClient.POST('addItemToInvoice', { ...body }, invoiceDetails?.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					dispatch(fetchOrderPlaceWorkflow(orderId));
					toastSuccess(data.message);
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUpdateItemToInvoice = async (item) => {
		setIsLoading(true);
		const body = {
			name: item.itemName,
			quantity: item.quantity,
			unitPrice: item.unitPrice
		};
		await ApiClient.PUT('updateItemToInvoice', { ...body }, item.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					dispatch(fetchOrderPlaceWorkflow(orderId));
					toastSuccess(data.message);
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onRemoveInvoice = async (item, idx) => {
		setIsLoading(true);
		if (!item.id) {
			setIsLoading(false);
			const newData = { ...invoiceDetails };
			const invoiceItem = newData['invoiceItemResponseList'];
			const updatedItems = invoiceItem.splice(idx, 1);
			newData[idx] = updatedItems;
			setInvoiceDetails(newData);
			return;
		}
		await ApiClient.DELETE('deleteItemToInvoice', {}, item.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					dispatch(fetchOrderPlaceWorkflow(orderId));
					toastSuccess(data.message);
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onAddTermsToInvoice = async (item) => {
		setIsLoading(true);
		const body = {
			terms: item.term,
			termDetails: item.termDetails
		};
		await ApiClient.POST('addTermToInvoice', { ...body }, invoiceDetails?.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					dispatch(fetchOrderPlaceWorkflow(orderId));
					toastSuccess(data.message);
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUpdateTermsToInvoice = async (item) => {
		setIsLoading(true);
		const body = {
			terms: item.term,
			termDetails: item.termDetails
		};
		await ApiClient.PUT('updateTerm', { ...body }, item.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					dispatch(fetchOrderPlaceWorkflow(orderId));
					toastSuccess(data.message);
					getInvoiceDetailsInfo(invoiceDetails?.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onDateChange = (date) => {
		setDate(date);
		const body = {
			invoiceDate: date.format('DD/MM/YYYY')
		};
		onUpdateInvoice({ ...body });
		setIsEditDate(false);
	};

	const onFocusChange = ({ focused }) => {
		setFocused(focused);
	};

	const onAddBuyerAddress = () => {
		setIsAddAddress(!isAddAddress);
	};

	const onAddNewProduct = () => {
		let otherItem = { itemType: 'OTHER', itemName: '', quantity: null, unitPrice: null };
		const newItem = {
			...invoiceDetails,
			invoiceItemResponseList: [...invoiceDetails.invoiceItemResponseList, otherItem]
		};
		setInvoiceDetails(newItem);
		const index = newItem.invoiceItemResponseList.length;
		setCellId(`${index - 1}-newItem`);
		setIsAddNewInvoice(true);
	};

	const onChangeAddNewInvoice = (e, idx) => {
		let { id, value } = e.target;
		const newData = { ...invoiceDetails };
		const invoiceItem = newData['invoiceItemResponseList'];
		if (id === 'quantity') {
			invoiceItem[idx][id] = value;
		} else {
			invoiceItem[idx][id] = value;
		}
		newData[idx] = invoiceItem;
		setInvoiceDetails(newData);
	};
	const resetAll = () => {
		prevTarget.current = null;
		setCellId('');
		setIsUpdateInvoice(false);
		setNewTermCellId('');
		setIsUpdateTerm(false);
	};

	const onAddNewInvoice = (item) => {
		if (item.itemName && item.quantity && item.unitPrice) {
			if (item.id) {
				onUpdateItemToInvoice(item);
				setCellId('');
				setIsUpdateInvoice(false);
			} else {
				onAddItemToInvoice(item);
				setCellId('');
			}
		}
	};

	const onAddNewTerms = () => {
		let otherItem = { isDeleted: false, term: '', termDetails: '' };
		const newItem = {
			...invoiceDetails,
			termsAndConditionsResponseList: [...invoiceDetails.termsAndConditionsResponseList, otherItem]
		};
		setInvoiceDetails(newItem);
		const index = newItem.termsAndConditionsResponseList.length;
		setNewTermCellId(`${index - 1}-newItem`);
		setIsAddNewTerm(true);
	};

	const onChangeAddNewTerms = (e, idx) => {
		let { id, value } = e.target;
		const newData = { ...invoiceDetails };
		const termsItem = newData['termsAndConditionsResponseList'];
		termsItem[idx][id] = value;
		newData[idx] = termsItem;
		setInvoiceDetails(newData);
	};

	const onAddTerms = (item) => {
		if (item.term && item.termDetails) {
			if (item?.id) {
				onUpdateTermsToInvoice(item);
				setNewTermCellId('');
				setIsUpdateTerm(false);
			} else {
				onAddTermsToInvoice(item);
				setNewTermCellId('');
			}
		}
	};

	const onChangeTerms = async (item) => {
		setIsLoading(true);
		const params = `?termIdList=${[item.id]}&isDeleted=${!item.isDeleted}`;
		await ApiClient.DELETE('deleteTerm', {}, params)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					// dispatch(fetchOrderPlaceWorkflow(orderId));
					toastSuccess(data.message);
					let updatedList = invoiceDetails?.termsAndConditionsResponseList?.map((term) => {
						if (term.id === item.id) {
							term.isDeleted = !term.isDeleted;
						}
						return term;
					});
					setInvoiceDetails({
						...invoiceDetails,
						termsAndConditionsResponseList: updatedList
					});
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	// const onSelectAll = async () => {
	// 	const ids = invoiceDetails?.termsAndConditionsResponseList?.map((item) => item.id);
	// 	setIsLoading(true);
	// 	const params = `?termIdList=${ids}&isDeleted=${!selectAll}`;
	// 	await Http.DELETE('deleteTerm', {}, params)
	// 		.then(({ data }) => {
	// 			setIsLoading(false);
	// 			if (data && data.success) {
	// 				toastSuccess(data.message);
	// 				getInvoiceDetailsInfo(invoiceDetails?.id);
	// 			}
	// 		})
	// 		.catch(({ response }) => {
	// 			setIsLoading(false);
	// 			if (response && response.data && response.data.message) {
	// 				toastError(response.data.message);
	// 			} else {
	// 				toastError('Request was unsuccessful.');
	// 			}
	// 		});
	// };

	const onRequest = async () => {
		setIsLoading(true);
		await ApiClient.POST(
			'requestInvoice',
			{},
			orderId + `?entityId=${invoiceDetails.id}&documentType=PROFORMA_INVOICE`
		)
			.then(() => {
				setIsLoading(false);
				getInvoiceDetailsInfo(invoiceDetails?.id);
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onDownload = async () => {
		setStartLoader(true);
		await ApiClient.GET('downloadInvoice', invoiceDetails?.id)
			.then((response) => {
				if (response.data) {
					window.open(response.data, '_parent');
				}
				getInvoiceDetailsInfo(invoiceDetails?.id);
				dispatch(fetchOrderPlaceWorkflow(orderId)).finally(() => {
					setInitialLoading(false);
				});
				setStartLoader(false);
			})
			.catch((error) => {
				toastError(error.response.data.message);
				setStartLoader(false);
			});
	};

	const onFileUpload = (e) => {
		let file = e.target.files[0];
		if (isValidFile(file, 'imageAndPdf', VALID_FILE_SIZE)) {
			let data = {
				name: file.name,
				docMimeType: file.type,
				documentType: 'NITEX_SIGNED_PI',
				base64Str: '',
				size: file.size
			};
			let reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				data.base64Str = reader.result;
				onAdd(data);
			};
			reader.onerror = function (error) {
				toastError(error);
			};
		}
	};

	const onBuyerPiUpload = (e) => {
		let file = e.target.files[0];
		if (isValidFile(file, 'imageAndPdf', VALID_FILE_SIZE)) {
			let data = {
				name: file.name,
				docMimeType: file.type,
				documentType: 'BUYER_SIGNED_PI',
				base64Str: '',
				size: file.size
			};
			let reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				data.base64Str = reader.result;
				onAdd(data);
			};
			reader.onerror = function (error) {
				toastError(error);
			};
		}
	};

	const onAdd = async (data) => {
		setIsLoading(true);
		await ApiClient.POST(`${BASE_URL}/invoice/${invoiceDetails.id}/add-payment-doc`, data, '', true)
			.then(() => {
				setIsLoading(false);
				getInvoiceDetailsInfo(invoiceDetails?.id);
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const renderProductInvoices = (item, idx) => {
		return (
			<>
				{/* Reference Column */}
				<td>
					{item?.mergedFromOrderId ? (
						<Tooltip title="Go to PI" placement="top" arrow>
							<Link
								to={`/orders/view/${item?.mergedFromOrderId}?workTab=PI&routeFrom=SCPI&invoiceId=${item?.mergedFromInvoiceId}`}
							>
								<p className="gray_dark_02 mb-0">PO# {item?.poNumber}</p>
								<p className="gray_dark_02 mb-0">{item?.referenceNumber}</p>
							</Link>
						</Tooltip>
					) : (
						<>
							<div className="mb-1">
								{item?.isSet && setTag()} {item?.isPack && packTag()}
							</div>
							<p className="gray_dark_02 mb-0">PO# {item?.poNumber}</p>
							<p className="gray_dark_02 mb-0">{item?.referenceNumber}</p>
						</>
					)}
				</td>
				<td>
					{item?.isSet ? (
						<div>
							{item?.setMemberResponseList && <p>{item?.setMemberResponseList[0]?.market}</p>}
							{item?.setMemberResponseList?.map((memberCategory, index) => (
								<span className="gray_dark_02" key={_getKey()}>
									{`${memberCategory?.itemName}${
										index !== item?.setMemberResponseList.length - 1 ? ',' : ''
									} `}
								</span>
							))}
						</div>
					) : (
						<span className="gray_dark_02">
							{item?.market && `${item?.market}, `}
							{item?.itemName}
						</span>
					)}
				</td>
				{/* Colors column */}
				<td>
					<span className="gray_dark_02">{item?.colors}</span>
				</td>
				{/* Size Column */}
				<td>
					<span className="gray_dark_02">{item?.sizes}</span>
				</td>
				{/* Fabric Column */}
				<td>
					{item?.isSet ? (
						item?.setMemberResponseList?.map((memberOfSet) => (
							<ul key={_getKey()} className="with-bullet">
								<li>{memberOfSet?.fabricDetails}</li>
							</ul>
							// <span className="gray_dark_02">{memberOfSet?.fabricDetails}</span>
						))
					) : (
						<span className="gray_dark_02">{item?.fabricDetails}</span>
					)}
				</td>
				<td>
					<span className="gray_dark_01">
						{changeDateFormat(item?.deliveryDate, '', 'Do MMM, YY')}
					</span>
				</td>
				<td>
					<span className="gray_dark_02">{item?.quantity}</span>
				</td>
				<td>
					<span className="gray_dark_02">{item?.unitPrice}</span>
				</td>
				<td>
					<div className="d-flex">
						<span className="gray_dark_02">{convertNumberToUSFormat(item?.totalPrice)}</span>
						<img
							src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
							className="ml-2"
							alt="delete"
							onClick={() => onRemoveInvoice(item, idx)}
						/>
					</div>
				</td>
			</>
		);
	};

	const renderOtherInvoices = (item, idx) => {
		return (
			<>
				<td />
				<td
					className="cursor-pointer"
					onClick={() => {
						setCellId(`${idx}-newItem`);
						setIsUpdateInvoice(true);
					}}
				>
					<span className="gray_dark_02" />

					{cellId === `${idx}-newItem` || (isAdNewInvoice && cellId === `${idx}-newItem`) ? (
						<input
							type="text"
							autoComplete="off"
							id="itemName"
							value={item.itemName}
							onChange={(e) => onChangeAddNewInvoice(e, idx)}
							onFocus={(event) => (prevTarget.current = event.target.value)}
							onBlur={(event) => {
								if (!isValidInput(prevTarget, event)) {
									resetAll();
									return;
								}
								onAddNewInvoice(item, idx);
							}}
						/>
					) : (
						<span className="gray_dark_02">{item?.itemName}</span>
					)}
				</td>
				<td />
				<td />
				<td />
				<td />
				<td
					className="cursor-pointer"
					onClick={() => {
						setCellId(`${idx}-newItem`);
						setIsUpdateInvoice(true);
					}}
				>
					{cellId === `${idx}-newItem` || (isAdNewInvoice && cellId === `${idx}-newItem`) ? (
						<input
							type="number"
							autoComplete="off"
							id="quantity"
							placeholder="0"
							min={1}
							value={isNaN(item.quantity) ? null : item.quantity}
							onChange={(e) => onChangeAddNewInvoice(e, idx)}
							onFocus={(event) => (prevTarget.current = event.target.value)}
							onBlur={(event) => {
								if (!isValidInput(prevTarget, event)) {
									resetAll();
									return;
								}
								onAddNewInvoice(item, idx);
							}}
							onKeyPress={validateNumber}
						/>
					) : (
						<>
							<span className="gray_dark_02" />
							<span className="gray_dark_02">{item?.quantity}</span>
						</>
					)}
				</td>
				<td
					className="cursor-pointer"
					onClick={() => {
						setCellId(`${idx}-newItem`);
						setIsUpdateInvoice(true);
					}}
				>
					{cellId === `${idx}-newItem` || (isAdNewInvoice && cellId === `${idx}-newItem`) ? (
						<input
							type="number"
							autoComplete="off"
							id="unitPrice"
							placeholder="0"
							min={1}
							value={isNaN(item.unitPrice) ? null : item.unitPrice}
							onKeyPress={validateFloatNumber}
							onChange={(e) => onChangeAddNewInvoice(e, idx)}
							onFocus={(event) => (prevTarget.current = event.target.value)}
							onBlur={(event) => {
								if (!isValidInput(prevTarget, event)) {
									resetAll();
									return;
								}
								onAddNewInvoice(item, idx);
							}}
						/>
					) : (
						<>
							<span className="gray_dark_02" />
							<span className="gray_dark_02">{item?.unitPrice}</span>
						</>
					)}
				</td>
				<td>
					<div className="d-flex">
						<span className="gray_dark_02">
							{convertNumberToUSFormat(parseFloat(item.quantity * item.unitPrice))}
						</span>
						<img
							src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
							className="ml-2"
							alt="delete"
							onClick={() => onRemoveInvoice(item, idx)}
						/>
					</div>
				</td>
			</>
		);
	};

	const getItemTotal = (items) => {
		return items?.invoiceItemResponseList?.reduce((total, item) => {
			return parseInt(total) + parseInt(item.quantity);
		}, 0);
	};

	const getSelectedBuyerDetails = () => {
		return brandLocationList.find((item) => item.id == selectedByerAddress)?.title || 'Select address';
	};
	const getSelectedBuyerBankDetails = () => {
		return (
			brandLocationList.find((item) => item.id == selectedByerShippingAddress)?.title ||
			'Select address'
		);
	};

	const getSelectedBeneficiaryBankDetails = () => {
		return nitexBankDetails.find((item) => item.id == selectedBankDetails)?.title || 'Select address';
	};

	const settings = {
		dots: false,
		infinite: false,
		speed: 500,
		slidesToShow: 3,
		marginRight: 16,
		slidesToScroll: 3,
		initialSlide: 0,
		responsive: [
			{
				breakpoint: 1024,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 2,
					infinite: true,
					dots: true,
					initialSlide: 1
				}
			},
			{
				breakpoint: 767,
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1,
					initialSlide: 1
				}
			},
			{
				breakpoint: 600,
				settings: {
					slidesToShow: 1,
					slidesToScroll: 2,
					initialSlide: 1
				}
			},
			{
				breakpoint: 480,
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1
				}
			}
		]
	};

	const onMergePi = async (id) => {
		setIsLoading(true);
		await ApiClient.POST('invoiceAction', {}, `${id}/merge-to/${invoiceDetails?.id}`)
			.then(() => {
				setIsLoading(false);
				getInvoiceDetailsInfo(invoiceDetails?.id);
				toastSuccess('Merged');
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const renderStatusWiseDate = (item) => {
		return (
			<div className="task-status purple">
				<span className="status-btn d-flex">
					{capitalizeFirstLetter(item.approvalStatus)}
					<span className="mx-1">|</span>
					{changeDateFormat(
						item.approvalStatus === 'COMPLETED'
							? invoiceDetails?.completedDate
							: invoiceDetails?.requestedDate,
						'YYYY-MM-DD',
						'DD MMM'
					)}
				</span>
			</div>
		);
	};

	return (
		<LoaderComponent loading={isLoading}>
			{allInvoiceList.length > 0 && invoiceDetails && (
				<div className="create-order-container mt-5">
					<div className="factory-slider pt-2">
						<div className="factory-slider-row d-block">
							<Slider {...settings}>
								{allInvoiceList?.map((item) => (
									<div
										className={`one-fourth ${
											item.id === invoiceDetails.id ? 'active' : ''
										}`}
										key={item.id}
										onClick={() => onInvoiceDetailsClick(item)}
									>
										<div className="f-info">
											<span>{item?.invoiceNumber}</span>
										</div>
										<div className="f-status d-flex justify-content-between align-items-center mt-4">
											<p className="price-and-design regular-14">
												<span>{item?.totalQuantity} pcs, </span>$
												{parseFloat(item.totalAmount).toFixed(2)}
												<span>({checkSingular('Design', stylesCount)})</span>
											</p>

											{item?.approvalStatus === 'PENDING' ? (
												<div className="task-status blue">
													<span className="status-btn d-flex">
														{capitalizeFirstLetter(item.approvalStatus)}
													</span>
												</div>
											) : (
												renderStatusWiseDate(item)
											)}
										</div>
									</div>
								))}
							</Slider>
						</div>
					</div>
					<div className="proforma-invoice-tab">
						{checkAnyUserType(['ADMIN', 'COMMERCIAL']) && (
							<div className="order-assigned-people align-items-center">
								<button
									className={
										orderStage === 'PENDING'
											? 'button gray-outline size32 extra-margin-right'
											: 'button gray-outline size32'
									}
									onClick={onDownload}
								>
									Download PI
									<img
										src={`${process.env.PUBLIC_URL}/icons/download-dark-20.svg`}
										alt="download"
										className="ml-1"
									/>
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			{invoiceDetails && (
				<div className="create-order-container">
					<div className="proforma-invoice-tab">
						<div className="section-heading d-flex justify-content-between align-items-center">
							<div className="request-status">
								{checkAnyUserType(['ADMIN', 'ACCOUNT_MANAGER']) && (
									<div className="d-flex">
										{invoiceDetails.approvalStatus === 'PENDING' && (
											<button className="button size32 mr-1" onClick={onRequest}>
												Request for commercial approval
											</button>
										)}
										{(invoiceDetails.approvalStatus === 'REQUESTED' ||
											invoiceDetails.approvalStatus === 'DOWNLOADED') && (
											<div className="request-sent-state d-flex align-items-center">
												<span className="regular-14 gray_dark_01">
													Sent to commercial
												</span>
												<span className="common-line-devider"></span>
												<span className="regular-14 gray_dark_02">
													{changeDateFormat(
														invoiceDetails?.requestedDate,
														'YYYY-MM-DD',
														'DD MMM, YYYY'
													)}
												</span>
											</div>
										)}
									</div>
								)}
							</div>
						</div>

						<div className="invoice-info same-space d-flex">
							<div className="d-flex invoice-left">
								<div className="invoice-heaing">
									<h3 className="gray_dark_01">INVOICE</h3>
								</div>
								<div className="invoice-details">
									<p className="regular-16 gray_dark_01 mb-0">{invoiceDetails.invoiceNo}</p>
									<p className="regular-14 gray_dark_01">
										Invoice date:{' '}
										<span>
											{changeDateFormat(
												invoiceDetails.date,
												'DD-MM-YYYY',
												'Do MMM, YY'
											)}
										</span>{' '}
										<img
											src={`${process.env.PUBLIC_URL}/icons/edit-black-20.svg`}
											alt="edit"
											onClick={() => setIsEditDate(!isEditDate)}
										/>
									</p>
									{isEditDate && (
										<SingleDatePicker
											daySize={35}
											isOutsideRange={() => false}
											date={date}
											placeholder="Date"
											onOutsideClick={true}
											numberOfMonths={1}
											onDateChange={(date) => onDateChange(date)}
											focused={focused}
											onFocusChange={onFocusChange}
											noBorder={true}
											regular={true}
										/>
									)}
								</div>
							</div>
							<div className="upload-files invoice-right d-flex">
								<div className="nitex-pi one-half">
									<p className="regular-14 gray_dark_02">Nitex signed PI</p>
									<div className="upload-pi-invoices d-flex align-items-center mt-1 ">
										{checkAnyUserType(['ADMIN', 'COMMERCIAL']) && (
											<div className="upload-pi">
												<label
													htmlFor="pi-upload"
													className="drag-upload cursor-pointer d-flex mb-0 mr-2 pr-1"
												>
													<img
														src={`${process.env.PUBLIC_URL}/icons/upload-dark.svg`}
														alt="upload"
														className="upload-icon"
													/>
													<p className="regular-14 gray_dark_01 ml-1">Upload</p>
												</label>
												<input
													type="file"
													className="file-upload d-none"
													onClick={(e) => (e.target.value = null)}
													onChange={(e) => onFileUpload(e)}
													multiple={false}
													name="pi-upload"
													id="pi-upload"
													accept="*"
												/>
											</div>
										)}

										<div className="uploaded-pi">
											{invoiceDetails.nitexSignedPI &&
												Object.keys(invoiceDetails.nitexSignedPI).length !== 0 && (
													<div
														className="chip-square-gray cursor-pointer"
														onClick={() =>
															window.open(
																invoiceDetails.nitexSignedPI?.docUrl,
																'_blank'
															)
														}
													>
														<Tooltip
															title={invoiceDetails.nitexSignedPI?.name}
															placement="top"
															arrow
														>
															<p className="regular-14 gray_dark_01">
																{getShortName(
																	invoiceDetails.nitexSignedPI?.name,
																	10
																)}{' '}
																|{' '}
																{changeDateFormat(
																	invoiceDetails.nitexSignedPI?.dateAdded,
																	'YYYY-MM-DD',
																	'DD MMM YY'
																)}
															</p>
														</Tooltip>
													</div>
												)}
										</div>
									</div>
								</div>
								<div className="buyer-pi one-half">
									<p className="regular-14 gray_dark_02">Buyer signed PI</p>
									<div className="upload-pi-invoices d-flex align-items-center mt-1 ">
										{invoiceDetails.nitexSignedPI &&
											Object.keys(invoiceDetails.nitexSignedPI).length !== 0 && (
												<div className="upload-pi">
													<label
														htmlFor="buyer-pi-upload"
														className="drag-upload cursor-pointer d-flex mb-0 mr-2 pr-1"
													>
														<img
															src={`${process.env.PUBLIC_URL}/icons/upload-dark.svg`}
															alt="upload"
															className="upload-icon"
														/>
														<p className="regular-14 gray_dark_01 ml-1">Upload</p>
													</label>
													<input
														type="file"
														className="file-upload d-none"
														onClick={(e) => (e.target.value = null)}
														onChange={(e) => onBuyerPiUpload(e)}
														multiple={false}
														name="buyer-pi-upload"
														id="buyer-pi-upload"
														accept="*"
													/>
												</div>
											)}

										<div className="uploaded-pi">
											{invoiceDetails.buyerSignedPI &&
												Object.keys(invoiceDetails.buyerSignedPI).length !== 0 && (
													<div
														className="chip-square-gray cursor-pointer"
														onClick={() =>
															window.open(
																invoiceDetails.buyerSignedPI?.docUrl,
																'_blank'
															)
														}
													>
														<Tooltip
															title={invoiceDetails.buyerSignedPI?.name}
															placement="top"
															arrow
														>
															<p className="regular-14 gray_dark_01">
																{getShortName(
																	invoiceDetails.buyerSignedPI?.name,
																	10
																)}{' '}
																|{' '}
																{changeDateFormat(
																	invoiceDetails.buyerSignedPI?.dateAdded,
																	'YYYY-MM-DD',
																	'DD MMM YY'
																)}
															</p>
														</Tooltip>
													</div>
												)}
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="buyer-and-beneficiary-section with-title d-flex justify-content-between">
							<div className="one-half same-space">
								<div className="one-half-title">
									<h3 className="semibold-18 mb-0">Buyer</h3>
								</div>
								<div className="info-heading d-flex justify-content-between">
									<p className="semibold-14 mb-0">Buyer details</p>
									<div className="option">
										<div className="dropdown">
											<button
												className="btn btn-default dropdown-toggle gray_dark_02 regular-14 pt-0"
												type="button"
												data-toggle="dropdown"
												aria-expanded="false"
											>
												{getSelectedBuyerDetails()}
												<img
													src={`${process.env.PUBLIC_URL}/icons/down-arrow.png`}
													className="ml-2"
													alt="down"
												/>
											</button>
											<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
												<li className="px-4 pb-3 pt-3">
													<a
														href={void 0}
														className
														onClick={() => onAddBuyerAddress()}
													>
														<img
															src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
															className="mr-2"
															alt="add"
														/>
														Add address
													</a>
												</li>

												{brandLocationList.map((item) => (
													<li
														className="px-4 pb-3 pt-3"
														key={item.id}
														onClick={() => {
															onUpdateBuyerDetails(item.id, 'BILLING');
															setSelectedBuyerAddress(item.id);
														}}
													>
														<a href={void 0} className="gray_dark_02">
															{item.title}
														</a>
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>
								<div className="info-details">
									<p className="regular-14 gray_dark_01">{invoiceDetails.brandName}</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerBillingLocation?.addressLine1}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerBillingLocation?.addressLine2}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerBillingLocation?.city}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerBillingLocation?.state}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerBillingLocation?.countryName}
									</p>
									<p className="regular-14 gray_dark_01">{invoiceDetails?.phone}</p>
									<p className="regular-14 gray_dark_01">{invoiceDetails?.email}</p>
								</div>
							</div>
							<div className="one-half same-space">
								<div className="one-half-title">
									<h3 className="semibold-18 mb-0">Beneficiary</h3>
								</div>
								<div className="info-heading d-flex justify-content-between">
									<p className="semibold-14 mb-0">Beneficiary details</p>
									<div className="option">
										<div className="dropdown">
											<button
												className="btn btn-default dropdown-toggle gray_dark_02 regular-14 pt-0"
												type="button"
												data-toggle="dropdown"
												aria-expanded="false"
											>
												{selectedBeneficiary ? selectedBeneficiary : 'Select address'}
												<img
													src={`${process.env.PUBLIC_URL}/icons/down-arrow.png`}
													className="ml-2"
													alt="down"
												/>
											</button>
											<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
												{beneficiaryList.map((item) => (
													<li
														className="px-4 pb-3 pt-3"
														key={item.id}
														onClick={() => {
															onUpdateBeneficiaryList(item, invoiceDetails.id);
															setSelectedBeneficiary(item.title);
														}}
													>
														<a href={void 0} className="gray_dark_02">
															{item.title}
														</a>
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>
								<div className="info-details">
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.beneficiaryDetails?.name}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.beneficiaryDetails?.address}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.beneficiaryDetails?.website}
									</p>
								</div>
							</div>
						</div>
						<div className="buyer-and-beneficiary-section d-flex justify-content-between">
							<div className="one-half same-space">
								<div className="info-heading d-flex justify-content-between">
									<p className="semibold-14 mb-0">Buyer shipping address</p>
									<div className="option">
										<div className="dropdown">
											<button
												className="btn btn-default dropdown-toggle gray_dark_02 regular-14 pt-0"
												type="button"
												data-toggle="dropdown"
												aria-expanded="false"
											>
												{getSelectedBuyerBankDetails()}
												<img
													src={`${process.env.PUBLIC_URL}/icons/down-arrow.png`}
													className="ml-2"
													alt="down"
												/>
											</button>
											<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
												<li className="px-4 pb-3 pt-3">
													<a
														href={void 0}
														className
														onClick={() => onAddBuyerAddress()}
													>
														<img
															src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
															className="mr-2"
															alt="add"
														/>
														Add address
													</a>
												</li>
												{brandLocationList.map((item) => (
													<li
														className="px-4 pb-3 pt-3"
														key={item.id}
														onClick={() => {
															onUpdateBuyerDetails(item.id, 'SHIPPING');
															setSelectedBuyerShippingAddress(item.id);
														}}
													>
														<a href={void 0} className="gray_dark_02">
															{item.title}
														</a>
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>
								<div className="info-details">
									<p className="regular-14 gray_dark_01">{invoiceDetails.brandName}</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerShippingLocation?.addressLine1}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerShippingLocation?.addressLine2}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerShippingLocation?.city}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerShippingLocation?.state}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.buyerShippingLocation?.countryName}
									</p>
									<p className="regular-14 gray_dark_01">{invoiceDetails?.phone}</p>
									<p className="regular-14 gray_dark_01">{invoiceDetails?.email}</p>
								</div>
							</div>
							<div className="one-half same-space">
								<div className="info-heading d-flex justify-content-between">
									<p className="semibold-14 mb-0">Beneficiary bank details</p>
									<div className="option">
										<div className="dropdown">
											<button
												className="btn btn-default dropdown-toggle gray_dark_02 regular-14 pt-0"
												type="button"
												data-toggle="dropdown"
												aria-expanded="false"
											>
												{/* {selectedBankDetails ? selectedBankDetails : 'Select address'} */}
												{getSelectedBeneficiaryBankDetails()}
												<img
													src={`${process.env.PUBLIC_URL}/icons/down-arrow.png`}
													className="ml-2"
													alt="down"
												/>
											</button>
											<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
												{nitexBankDetails.map((item) => (
													<li
														className="px-4 pb-3 pt-3"
														key={item.id}
														onClick={() => {
															onUpdateNitexBankDetails(item, invoiceDetails.id);
															setSelectedBankDetails(item.id);
														}}
													>
														<a href={void 0} className="gray_dark_02">
															{item.title}
														</a>
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>
								<div className="info-details">
									<p className="regular-14 gray_dark_01">
										<span>Swift:</span> {invoiceDetails.beneficiaryBankDetails?.swiftCode}
									</p>
									<p className="regular-14 gray_dark_01">
										<span>A/C:</span>{' '}
										{invoiceDetails.beneficiaryBankDetails?.accountNumber}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.beneficiaryBankDetails?.bankName}
									</p>
									<p className="regular-14 gray_dark_01">
										{invoiceDetails.beneficiaryBankDetails?.bankDetails}
									</p>
								</div>
							</div>
						</div>
						{/* Start Product & service details */}
						<div className="product-and-service-details">
							<div className="d-flex justify-content-between">
								<div className="title">
									<h3 className="semibold-18">Product &amp; service details</h3>
								</div>

								<div className="single-filter mb-3">
									<CustomDropdown
										title={'Merge PI'}
										onItemClick={(item) => onMergePi(item.id)}
										items={allOrdersInvoiceList}
										customDesign={(item) => getCard(item)}
										selectedItem={null}
										onItemSearch={onSearchPi}
									/>
								</div>
							</div>
							<div className="service-details-table scroll-x-label">
								<table className="table">
									<thead>
										<tr>
											<th>
												<span className="semibold-14">SL</span>
											</th>
											<th>
												<span className="semibold-14">Reference</span>
											</th>
											<th>
												<span className="semibold-14">Description</span>
											</th>
											<th>
												<span className="semibold-14">Color</span>
											</th>
											<th>
												<span className="semibold-14">Sizes</span>
											</th>
											<th>
												<span className="semibold-14">Fabric</span>
											</th>
											<th>
												<span className="semibold-14">ETD</span>
											</th>
											<th>
												<span className="semibold-14">Qty</span>
											</th>
											<th>
												<span className="semibold-14">Unit price</span>
											</th>
											<th>
												<span className="semibold-14">Amount</span>
											</th>
										</tr>
									</thead>
									<tbody>
										{invoiceDetails?.invoiceItemResponseList?.map((item, idx) => (
											<tr key={item.id}>
												<td>
													<span className="gray_dark_02">{idx + 1}</span>
												</td>
												{item.itemType === 'PRODUCT'
													? renderProductInvoices(item, idx)
													: renderOtherInvoices(item, idx)}
											</tr>
										))}
										<tr>
											<td colSpan={2}>
												<button
													className="button text pl-0"
													onClick={onAddNewProduct}
												>
													<span className="ml-1">Add row</span>
													<img
														src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
														alt="add"
													/>
												</button>
											</td>
											<td />
											<td />
											<td />
											<td>
												<span className="semibold-14">Total:</span>
											</td>
											<td />
											<td>
												<span className="semibold-14">
													{isNaN(getItemTotal(invoiceDetails))
														? null
														: parseInt(getItemTotal(invoiceDetails))}
												</span>
											</td>
											<td>{/* <span className="semibold-14">$300.45</span> */}</td>
											<td>
												<span className="semibold-14">
													${convertNumberToUSFormat(invoiceDetails.amount)}
												</span>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						{/* Terms and conditions */}
						<div className="product-and-service-details terms-and-conditions">
							<div className="title">
								<h3 className="semibold-18">Terms and conditions</h3>
							</div>

							<div className="service-details-table terms-and-conditions-table">
								<table className="table">
									<thead>
										<tr>
											<th>
												{/*<div className="custom-chekbox">*/}
												{/*	<div className="form-group m-0">*/}
												{/*		<input*/}
												{/*			type="checkbox"*/}
												{/*			id="t1"*/}
												{/*			name="terms"*/}
												{/*			checked={!selectAll}*/}
												{/*		/>*/}
												{/*		<label*/}
												{/*			htmlFor="t1"*/}
												{/*			onClick={() => {*/}
												{/*				onSelectAll();*/}
												{/*			}}*/}
												{/*		/>*/}
												{/*	</div>*/}
												{/*</div>*/}
											</th>
											<th>
												<span className="semibold-14">SL</span>
											</th>
											<th>
												<span className="semibold-14">Title</span>
											</th>
											<th>
												<span className="semibold-14">Description</span>
											</th>
										</tr>
									</thead>
									<tbody>
										{invoiceDetails.termsAndConditionsResponseList?.map((item, idx) => (
											<tr key={item.id}>
												<td>
													<div className="custom-chekbox">
														<div className="form-group m-0">
															<input
																type="checkbox"
																id={item.id}
																name="terms"
																checked={!item.isDeleted}
															/>
															<label
																htmlFor="t2"
																onClick={() => onChangeTerms(item)}
															/>
														</div>
													</div>
												</td>
												<td>
													<span className="gray_dark_02">{idx + 1}</span>
												</td>
												<td
													onClick={() => {
														setNewTermCellId(`${idx}-newItem`);
														setIsUpdateTerm(true);
													}}
												>
													{newTermCellId === `${idx}-newItem` ||
													(isAddNewTerm && newTermCellId === `${idx}-newItem`) ? (
														<input
															type="text"
															autoComplete="off"
															id="term"
															value={item.term}
															onChange={(e) => onChangeAddNewTerms(e, idx)}
															// onBlur={() => onAddTerms(item, idx)}
															onFocus={(event) =>
																(prevTarget.current = event.target.value)
															}
															onBlur={(event) => {
																if (!isValidInput(prevTarget, event)) {
																	resetAll();
																	return;
																}
																onAddTerms(item, idx);
															}}
															onKeyPress={(e) => {
																if (e.key === 'Enter') {
																	if (!isValidInput(prevTarget, e)) {
																		resetAll();
																		return;
																	}
																	onAddTerms(item, idx);
																}
															}}
														/>
													) : (
														<span className="gray_dark_02">{item?.term}</span>
													)}
												</td>
												<td
													onClick={() => {
														setNewTermCellId(`${idx}-newItem`);
														setIsUpdateTerm(true);
													}}
												>
													{newTermCellId === `${idx}-newItem` ||
													(isAddNewTerm && newTermCellId === `${idx}-newItem`) ? (
														<input
															type="text"
															autoComplete="off"
															id="termDetails"
															value={item.termDetails}
															onChange={(e) => onChangeAddNewTerms(e, idx)}
															onFocus={(event) =>
																(prevTarget.current = event.target.value)
															}
															onBlur={(event) => {
																if (!isValidInput(prevTarget, event)) {
																	resetAll();
																	return;
																}
																onAddTerms(item, idx);
															}}
															onKeyPress={(e) => {
																if (e.key === 'Enter') {
																	if (!isValidInput(prevTarget, e)) {
																		resetAll();
																		return;
																	}
																	onAddTerms(item, idx);
																}
															}}
														/>
													) : (
														<span className="gray_dark_02">
															{item?.termDetails}
														</span>
													)}
												</td>
											</tr>
										))}

										<tr>
											<td colSpan={4}>
												<button className="button text pl-0" onClick={onAddNewTerms}>
													<span className="ml-1">Add row</span>
													<img
														src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
														alt="add"
													/>
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			)}
			{isAddAddress && (
				<AddBuyerAddress
					onAddBuyerAddress={onAddBuyerAddress}
					details={invoiceDetails}
					geDetailsInfo={getInvoiceDetailsInfo}
				/>
			)}
			{startLoader && <TimeBasedLoader startLoader={startLoader} />}
		</LoaderComponent>
	);
};

export default ProformaInvoice;
